import { useMemo, useState } from "react";
import { Check, Copy, UserX } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { UserMultiSelect } from "./MeetingUserMultiSelect";
import { useInviteToMeeting } from "../hooks/useMeetings";
import {
  useCreateGuestInvite,
  useGuestInvites,
  useRevokeGuestInvite,
} from "../hooks/useMeetings";

interface Props {
  open: boolean;
  onClose: () => void;
  meetingId: string;
  occurrenceId: string;
}

type Tab = "user" | "guest";

const INPUT =
  "rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-primary)]";

/**
 * "Zaproś" — one entry point, two ways: an internal user/contractor (added as a
 * participant + notified) or an external guest (a one-meeting link to send).
 */
export function InviteModal({ open, onClose, meetingId, occurrenceId }: Props) {
  const [tab, setTab] = useState<Tab>("user");
  const [selected, setSelected] = useState<string[]>([]);
  const [guestName, setGuestName] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const invite = useInviteToMeeting(meetingId);
  const { data: guestInvites = [] } = useGuestInvites(occurrenceId, open);
  const createGuest = useCreateGuestInvite(occurrenceId);
  const revokeGuest = useRevokeGuestInvite(occurrenceId);

  const latestValid = useMemo(
    () => guestInvites.filter((g) => g.is_valid),
    [guestInvites],
  );

  const submitUsers = async () => {
    if (selected.length === 0) return;
    await invite.mutateAsync(selected);
    setSelected([]);
    onClose();
  };

  const copy = async (id: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* clipboard blocked — the link is visible in the title attr */
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Zaproś na spotkanie" size="md">
      <div className="space-y-4">
        <div className="flex overflow-hidden rounded-md border border-[var(--color-border)] text-sm">
          {(
            [
              ["user", "Osoba z kontem"],
              ["guest", "Gość (link)"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              className={`flex-1 px-3 py-1.5 ${
                tab === value
                  ? "bg-[var(--color-primary)] text-white"
                  : "text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "user" ? (
          <div className="space-y-4">
            <p className="text-xs text-[var(--color-text-muted)]">
              Wybrana osoba zostanie dodana do spotkania i dostanie
              powiadomienie (a jeśli spotkanie trwa — dzwonek do dołączenia).
            </p>
            <UserMultiSelect selected={selected} onChange={setSelected} />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-black/5 dark:hover:bg-white/5"
              >
                Anuluj
              </button>
              <button
                type="button"
                disabled={invite.isPending || selected.length === 0}
                onClick={() => void submitUsers()}
                className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                Zaproś ({selected.length})
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[var(--color-text-muted)]">
              Link daje dostęp wyłącznie do tego jednego spotkania — bez konta i
              bez wglądu w cokolwiek innego. Wygasa godzinę po planowanym końcu.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Nazwa gościa (opcjonalnie)"
                className={`${INPUT} flex-1`}
              />
              <button
                type="button"
                disabled={createGuest.isPending}
                onClick={() =>
                  createGuest
                    .mutateAsync({ display_name: guestName.trim() })
                    .then(() => setGuestName(""))
                    .catch(() => {})
                }
                className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                Utwórz link
              </button>
            </div>

            {latestValid.length > 0 && (
              <ul className="space-y-1.5">
                {latestValid.map((g) => (
                  <li
                    key={g.id}
                    className="flex items-center gap-2 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate" title={g.link}>
                      {g.display_name || g.email || "Gość"}
                    </span>
                    <button
                      type="button"
                      onClick={() => void copy(g.id, g.link)}
                      className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
                    >
                      {copiedId === g.id ? (
                        <>
                          <Check size={12} className="text-emerald-600" />{" "}
                          Skopiowano
                        </>
                      ) : (
                        <>
                          <Copy size={12} /> Kopiuj link
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => void revokeGuest.mutateAsync(g.id)}
                      className="flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                    >
                      <UserX size={12} /> Unieważnij
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
